import { useCallback } from 'react'
import { Text, View } from 'react-native'
import { useRouter } from 'expo-router'
import { VoiceAssessmentIntroScreen } from '@/src/features/voice-assessment/screens/VoiceAssessmentIntroScreen'
import { getVoiceAssessmentVoiceType } from '@/src/features/voice-assessment/utils/voice-assessment-analytics'
import { buildVoiceAssessmentRoute } from '@/src/features/voice-assessment/utils/voice-assessment-route'
import { useAppSessionDefinitionId } from '@/src/hooks/useAppSessionDefinitionId'
import { useContentfulLocale } from '@/src/hooks/useContentfulLocale'
import { useRequiredAppSessionUser } from '@/src/hooks/useRequiredAppSessionUser'
import {
  sessionDefinitionQueryOptions,
  useStartSessionMutation,
} from '@/src/queries/session-queries'
import {
  buildVoiceAssessmentStartedProperties,
  MixpanelEvents,
  trackEvent,
} from '@/src/services/analytics'
import { showAlert } from '@/src/utils/alert'
import { captureException } from '@/src/utils/sentry'
import { useSuspenseQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import {
  countMeasurementsRequiringRecording,
  getProgressTotal,
} from './VoiceAssessmentFlow.shared'

const POST_PAYMENT_VOICE_ASSESSMENT_INTRO_STEP = 3

interface VoiceAssessmentIntroRouteProps {
  readonly isOnboardingEntry?: boolean
}

/**
 * Renders the standalone `/voice-assessment` intro.
 *
 * A new backend session is created only after the user explicitly starts.
 */
export function VoiceAssessmentIntroRoute({
  isOnboardingEntry = false,
}: VoiceAssessmentIntroRouteProps) {
  const { back, canGoBack, replace } = useRouter()
  const { t } = useTranslation(['common', 'onboarding'])
  const contentfulLanguage = useContentfulLocale()
  const { backendUser } = useRequiredAppSessionUser()
  const voiceType = getVoiceAssessmentVoiceType(backendUser)

  const sessionDefinitionId = useAppSessionDefinitionId()
  const sessionDefinitionQuery = useSuspenseQuery(
    sessionDefinitionQueryOptions(sessionDefinitionId, contentfulLanguage),
  )
  const startSessionMutation = useStartSessionMutation()

  const navigateAway = useCallback(() => {
    if (isOnboardingEntry) {
      replace('/(protected)/(account)/(tabs)')
      return
    }

    if (canGoBack()) {
      back()
      return
    }
    replace('/(protected)/(account)/(tabs)')
  }, [back, canGoBack, isOnboardingEntry, replace])

  const handleStartSession = useCallback(() => {
    if (startSessionMutation.isPending) {
      return
    }
    startSessionMutation.mutate(sessionDefinitionId, {
      onSuccess: (result) => {
        trackEvent(
          MixpanelEvents.VoiceAssessmentStarted,
          buildVoiceAssessmentStartedProperties({
            sessionId: result.sessionId,
            sessionDefinitionId,
            voiceType,
            totalMeasurements:
              sessionDefinitionQuery.data.measurementDefinitions.length,
          }),
        )
        replace(
          buildVoiceAssessmentRoute(result.sessionId, {
            source: isOnboardingEntry ? 'onboarding' : undefined,
          }),
        )
      },
      onError: (error) => {
        captureException(error, {
          feature: 'voice-assessment',
          tags: { action: 'start_voice_assessment_session' },
        })
        showAlert(
          t('common:error'),
          t('onboarding:voice_assessment.start_session_failed_message'),
        )
      },
    })
  }, [
    isOnboardingEntry,
    replace,
    sessionDefinitionId,
    sessionDefinitionQuery.data.measurementDefinitions.length,
    startSessionMutation,
    t,
    voiceType,
  ])

  const measurementDefinitions =
    sessionDefinitionQuery.data.measurementDefinitions
  const measurementCount = measurementDefinitions.length

  if (measurementCount === 0) {
    return (
      <View className="flex-1 items-center justify-center px-6">
        <Text className="text-base text-textBody text-center">
          {t('onboarding:voice_assessment.no_measurements_message')}
        </Text>
      </View>
    )
  }

  const totalProgressSteps = getProgressTotal(measurementDefinitions)
  const recordingMeasurementCount = countMeasurementsRequiringRecording(
    measurementDefinitions,
  )
  const hasRecordingInstructionsStep = recordingMeasurementCount > 0
  const postPaymentTotalProgressSteps =
    POST_PAYMENT_VOICE_ASSESSMENT_INTRO_STEP +
    totalProgressSteps +
    (hasRecordingInstructionsStep ? 1 : 0)

  return (
    <VoiceAssessmentIntroScreen
      onStart={handleStartSession}
      onSkip={navigateAway}
      isStartingSession={startSessionMutation.isPending}
      expectationStandalone={{
        totalSteps: totalProgressSteps,
        measurementCount: recordingMeasurementCount,
      }}
      timeEstimateMinutes={sessionDefinitionQuery.data.timeEstimate}
      totalSteps={isOnboardingEntry ? postPaymentTotalProgressSteps : undefined}
      currentStep={
        isOnboardingEntry ? POST_PAYMENT_VOICE_ASSESSMENT_INTRO_STEP : undefined
      }
      isPostPaymentFlow={isOnboardingEntry}
    />
  )
}
